import React from "react";
import { useOnboarding } from "../providers/onboarding-provider";
import { StepProgressBar } from "./onboarding/StepProgressBar";

/**
 * Member self-serve onboarding progress bar.
 *
 * Sources its step list + availability from `useOnboarding()` and
 * delegates rendering to the shared `StepProgressBar` primitive so the
 * member flow and the clinical-site iPad runner stay visually
 * identical (one CSS file, one DOM shape).
 */
export const OnboardingProgressBar = () => {
  const {
    currentStep,
    availableSteps,
    ONBOARDING_STEPS,
    stepStatuses,
    stepSequence,
  } = useOnboarding();

  if (currentStep === "INELIGIBLE") {
    return null;
  }

  let steps;
  if (stepSequence && stepSequence.length > 0) {
    // Server-driven steps. The Consent step gets `?source=manual` so
    // re-visiting it from the bar lands on the "you've already signed"
    // branch instead of auto-advancing the user.
    steps = stepSequence.map((step) => ({
      id: step.id,
      label: step.label,
      path: step.id === "CONSENT" ? "/consent?source=manual" : step.path,
    }));
  } else {
    // Fallback for older backends that don't ship `stepSequence`.
    const requiresApproval = stepStatuses && stepStatuses.PENDING_APPROVAL;
    steps = [
      { id: "PRE_SCREEN", label: "Pre-Screen", path: "/pre-screen" },
      { id: "CONSENT", label: "Consent", path: "/consent?source=manual" },
      { id: "VERIFY", label: "Verification", path: "/verify" },
      { id: "PREFERENCES", label: "Preferences", path: "/preferences" },
      { id: "DONE", label: "Complete", path: "/thank-you" },
    ];
    if (requiresApproval) {
      steps.splice(1, 0, {
        id: "PENDING_APPROVAL",
        label: "Approval",
        path: "/pending-approval",
      });
    }
  }

  // The legacy "is past" rule used the per-step `order` field so that
  // out-of-band gates (PENDING_APPROVAL inserted mid-flow) still show
  // their checkmarks correctly. Translate that into an explicit
  // availability list rather than relying on array index ordering.
  const availableStepIds = steps
    .filter((step) => availableSteps?.includes(step.id))
    .map((step) => step.id);

  return (
    <StepProgressBar
      steps={steps}
      currentStepId={currentStep}
      availableStepIds={availableStepIds}
      getStepHref={(step) => step.path}
    />
  );
};
